import { computed } from 'vue'
import '@core/global' // window helpers ($ajaxGet, dayjs, ...) that the filters and stores use
import filters from '@core/filters'
import { createLocale } from '@core/scripts/translate'
import Modal from '@core/components/modal/Modal.vue'
import NoData from '@core/components/NoData.vue'
import Loader from '@core/components/Loader.vue'
import CoreLoader from '@core/components/CoreLoader.vue'
import FilePreview from '@core/components/FilePreview.vue'

// Same as @core/scripts/translate.ts: the text shown for a key before translations load.
const toTitle = (text: string) => {
    const t = text
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/-/g, ' ')

    return t.substring(0, 1).toUpperCase() + t.substring(1)
}

// What @core's LoadGeneral plugin installs, minus createPinia (@pinia/nuxt creates a store
// per request) and the session restore (plugins/04.session.client.ts).
export default defineNuxtPlugin((nuxtApp) => {
    const app = nuxtApp.vueApp
    const url = useRequestURL({ xForwardedHost: true })

    if (import.meta.client) {
        app.use(createLocale({}))
    } else {
        // @core's translations load in the browser (IndexedDB + /auth/translations), after
        // hydration. Render what the browser shows until then: the key's last segment as a title.
        const t = (key?: string | null, args?: Record<string, any> | string | null) =>
            key ? toTitle(key.split('.').pop() || key) : (typeof args === 'string' ? args : '-')

        app.config.globalProperties.$t = t
        app.provide('$translate', t)
    }

    app.component('modal', Modal)
    app.component('no-data', NoData)
    app.component('preview-file', FilePreview)
    app.component('file-preview', FilePreview)
    app.component('loader', Loader)
    app.component('core-loader', CoreLoader)

    app.config.globalProperties.$baseUrl = url.origin
    app.config.globalProperties.$currentHost = url.host
    app.config.globalProperties.$prefixUrl = import.meta.env.VITE_APP_BASE_URL
    app.config.globalProperties.$moduleName = import.meta.env.VITE_MODULE_NAME
    app.config.globalProperties.$isDesktop = false
    app.config.globalProperties.$filter = filters
    app.config.globalProperties.$isPermitted = import.meta.client
        ? window.$isPermitted
        : (permission?: string | string[] | null, defaultValue = false) => !permission || defaultValue
    app.config.globalProperties.$isMobile = computed(() => import.meta.client && window.innerWidth <= 768)
})
